import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
  InputAdornment,
  Grid
} from '@mui/material';
import { saveServicePrice, getAppointmentTypes } from './servicePriceService';

interface ServicePriceDialogProps {
  open: boolean;
  onClose: (refreshNeeded: boolean) => void;
  price: {
    id: number;
    appointment_type_id: string;
    appointment_type_name: string;
    price: number | null;
    custom_description: string | null;
  } | null;
}

interface AppointmentType {
  id: string;
  name: string;
}

const ServicePriceDialog: React.FC<ServicePriceDialogProps> = ({
  open,
  onClose,
  price
}) => {
  const [appointmentTypeId, setAppointmentTypeId] = useState('');
  const [appointmentTypeName, setAppointmentTypeName] = useState('');
  const [priceValue, setPriceValue] = useState<string>('');
  const [customDescription, setCustomDescription] = useState('');
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState<{
    appointmentTypeId?: string;
    price?: string;
  }>({});
  
  useEffect(() => {
    const fetchAppointmentTypes = async () => {
      try {
        setLoadingTypes(true);
        const result = await getAppointmentTypes();
        setAppointmentTypes(result.appointment_types || []);
      } catch (err: any) {
        setError('Failed to load appointment types: ' + err.message);
      } finally {
        setLoadingTypes(false);
      }
    };
    
    fetchAppointmentTypes();
  }, []);
  
  useEffect(() => {
    if (price) {
      setAppointmentTypeId(price.appointment_type_id);
      setAppointmentTypeName(price.appointment_type_name);
      setPriceValue(price.price !== null ? price.price.toString() : '');
      setCustomDescription(price.custom_description || '');
    } else {
      setAppointmentTypeId('');
      setAppointmentTypeName('');
      setPriceValue('');
      setCustomDescription('');
    }
  }, [price]);
  
  const handleSubmit = async () => {
    // Validate form
    const errors: {
      appointmentTypeId?: string;
      price?: string;
    } = {};
    
    if (!appointmentTypeId) {
      errors.appointmentTypeId = 'Please select a service';
    }
    
    if (priceValue && isNaN(Number(priceValue))) {
      errors.price = 'Price must be a number';
    }
    
    setFormErrors(errors);
    
    // If there are errors, don't submit
    if (Object.keys(errors).length > 0) {
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Convert price to number or null
      const numericPrice = priceValue ? parseFloat(priceValue) : null;
      
      // Get appointment type name if it's not set (for new records)
      let typeName = appointmentTypeName;
      if (!typeName && appointmentTypeId) {
        const foundType = appointmentTypes.find(t => t.id === appointmentTypeId);
        typeName = foundType ? foundType.name : '';
      }
      
      // Create data object
      const data = {
        id: price?.id,
        appointment_type_id: appointmentTypeId,
        appointment_type_name: typeName,
        price: numericPrice,
        custom_description: customDescription || null
      };
      
      // Save the price
      await saveServicePrice(data);
      
      // Close dialog and refresh list
      onClose(true);
    } catch (err: any) {
      setError(err.message || 'Failed to save service price');
    } finally {
      setLoading(false);
    }
  };
  
  const handleAppointmentTypeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const id = event.target.value as string;
    setAppointmentTypeId(id);
    
    // Also update the name from the selected type
    const selectedType = appointmentTypes.find(type => type.id === id);
    if (selectedType) {
      setAppointmentTypeName(selectedType.name);
    }
  };
  
  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {price ? 'Edit Service Price' : 'Add Service Price'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <FormControl fullWidth error={!!formErrors.appointmentTypeId} disabled={loadingTypes || !!price}>
              <InputLabel id="appointment-type-label">Service</InputLabel>
              <Select
                labelId="appointment-type-label"
                value={appointmentTypeId}
                onChange={handleAppointmentTypeChange as any}
                label="Service"
                startAdornment={
                  loadingTypes ? (
                    <InputAdornment position="start">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : null
                }
              >
                {appointmentTypes.map((type) => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name}
                  </MenuItem>
                ))}
              </Select>
              {formErrors.appointmentTypeId && (
                <FormHelperText>{formErrors.appointmentTypeId}</FormHelperText>
              )}
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              label="Price"
              fullWidth
              value={priceValue}
              onChange={(e) => setPriceValue(e.target.value)}
              type="number"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              error={!!formErrors.price}
              helperText={formErrors.price}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Custom Description"
              fullWidth
              multiline
              rows={4}
              value={customDescription}
              onChange={(e) => setCustomDescription(e.target.value)}
              placeholder="Optional description that will override the default service description"
            />
          </Grid>
        </Grid>
        
        {error && (
          <FormHelperText error sx={{ mt: 2 }}>
            {error}
          </FormHelperText>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServicePriceDialog;